import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import './GitHubIntegration.css';

const GitHubIntegration = ({ projectId }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [config, setConfig] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [selectedInstallationId, setSelectedInstallationId] = useState('');
  const [repositories, setRepositories] = useState([]);
  const [selectedRepoId, setSelectedRepoId] = useState('');
  const [connectedRepo, setConnectedRepo] = useState(null);
  const [branches, setBranches] = useState([]);
  const [issues, setIssues] = useState([]);
  const [pullRequests, setPullRequests] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [installationClaiming, setInstallationClaiming] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [connectionMessage, setConnectionMessage] = useState('');
  const { showSuccess, showError } = useNotification();

  const selectedInstallation = installations.find(
    (installation) => String(installation.installation_id) === String(selectedInstallationId)
  );
  const selectedInstallationNeedsClaim = selectedInstallation &&
    !selectedInstallation.installed_by_user_id &&
    !selectedInstallation.company_id;

  const claimInstallationFromUrl = useCallback(async () => {
    if (typeof window === 'undefined') return false;

    const params = new URLSearchParams(window.location.search || '');
    const installationId = params.get('installationId');
    const githubAppStatus = params.get('githubApp');
    if (githubAppStatus !== 'installed' || !installationId) {
      return false;
    }

    try {
      await axios.post(getApiUrl(`github-app/installations/${installationId}/claim`));
      setSelectedInstallationId(String(installationId));
      params.delete('githubApp');
      params.delete('installationId');
      params.delete('projectId');
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
      showSuccess('GitHub App instalada e associada ao seu usuario');
      return true;
    } catch (error) {
      console.error('Erro ao associar instalacao GitHub App:', error);
      showError('A GitHub App foi instalada, mas nao foi possivel associar ao usuario atual');
      return false;
    }
  }, [showError, showSuccess]);

  const loadRepositoryData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const [branchesResponse, issuesResponse, pullsResponse, releasesResponse] = await Promise.all([
        axios.get(getApiUrl(`github-app/projects/${projectId}/branches`)),
        axios.get(getApiUrl(`github-app/projects/${projectId}/issues`)),
        axios.get(getApiUrl(`github-app/projects/${projectId}/pull-requests`)),
        axios.get(getApiUrl(`github-app/projects/${projectId}/releases`))
      ]);

      setBranches(branchesResponse.data.data || []);
      setIssues(issuesResponse.data.data || []);
      setPullRequests(pullsResponse.data.data || []);
      setReleases(releasesResponse.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados do GitHub App:', error);
      showError('Erro ao carregar dados do GitHub');
    } finally {
      setLoading(false);
    }
  }, [projectId, showError]);

  const loadInstallations = useCallback(async () => {
    setConnectionMessage('');
    try {
      const response = await axios.get(getApiUrl('github-app/installations?refresh=true'));
      const data = response.data.data || [];
      setInstallations(data);
      setSelectedInstallationId((current) => current || (data.length === 1 ? String(data[0].installation_id) : ''));
      if (data.length === 0) {
        setConnectionMessage('Nenhuma instalacao GitHub encontrada para esta conta. Rode o diagnostico para ver o motivo tecnico.');
      } else {
        setConnectionMessage(`${data.length} instalacao(oes) GitHub encontrada(s).`);
      }
    } catch (error) {
      console.error('Erro ao carregar instalacoes GitHub App:', error);
      const detail = error?.response?.data?.details || error?.response?.data?.error || error.message;
      setConnectionMessage(`Falha ao atualizar conexoes GitHub: ${detail}`);
      showError(`Falha ao atualizar conexoes GitHub: ${detail}`);
    }
  }, [showError]);

  const runDiagnostics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(getApiUrl('github-app/diagnostics'));
      setDiagnostics(response.data.data || null);
      showSuccess('Diagnostico GitHub executado');
    } catch (error) {
      console.error('Erro ao executar diagnostico GitHub App:', error);
      const detail = error?.response?.data?.details || error?.response?.data?.error || error.message;
      setConnectionMessage(`Erro no diagnostico GitHub: ${detail}`);
      showError(`Erro no diagnostico GitHub: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const claimSelectedInstallation = async (installationId = selectedInstallationId) => {
    if (!installationId) {
      showError('Selecione uma instalacao GitHub');
      return;
    }

    setInstallationClaiming(true);
    try {
      await axios.post(getApiUrl(`github-app/installations/${installationId}/claim`));
      setSelectedInstallationId(String(installationId));
      await loadInstallations();
      showSuccess('Instalacao GitHub confirmada na ferramenta');
    } catch (error) {
      console.error('Erro ao confirmar instalacao GitHub App:', error);
      showError('Nao foi possivel confirmar esta instalacao GitHub');
    } finally {
      setInstallationClaiming(false);
    }
  };

  const checkGitHubConnection = useCallback(async () => {
    try {
      await claimInstallationFromUrl();

      const [configResponse, statusResponse] = await Promise.all([
        axios.get(getApiUrl('github-app/config')),
        axios.get(getApiUrl(`github-app/projects/${projectId}/status`))
      ]);

      setConfig(configResponse.data.data || null);
      setIsConnected(!!statusResponse.data.connected);
      setConnectedRepo(statusResponse.data.data || null);

      if (statusResponse.data.connected) {
        loadRepositoryData();
      } else {
        loadInstallations();
      }
    } catch (error) {
      console.error('Erro ao verificar conexao GitHub App:', error);
      loadInstallations();
    }
  }, [projectId, claimInstallationFromUrl, loadInstallations, loadRepositoryData]);

  useEffect(() => {
    checkGitHubConnection();
  }, [checkGitHubConnection]);

  useEffect(() => {
    const loadRepositories = async () => {
      if (!selectedInstallationId || isConnected) return;
      const currentInstallation = installations.find(
        (installation) => String(installation.installation_id) === String(selectedInstallationId)
      );
      if (currentInstallation && !currentInstallation.installed_by_user_id && !currentInstallation.company_id) {
        setRepositories([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          getApiUrl(`github-app/installations/${selectedInstallationId}/repositories?refresh=true`)
        );
        const data = response.data.data || [];
        setRepositories(data);
        if (data.length === 1) {
          setSelectedRepoId(String(data[0].github_repo_id));
        }
      } catch (error) {
        console.error('Erro ao carregar repositorios GitHub App:', error);
        showError('Falha ao carregar repositorios do GitHub');
      } finally {
        setLoading(false);
      }
    };

    loadRepositories();
  }, [selectedInstallationId, installations, isConnected, showError]);

  const connectToGitHubApp = async () => {
    setLoading(true);
    try {
      const response = await axios.get(getApiUrl(`github-app/install?projectId=${projectId}`));
      if (response.data.installUrl) {
        window.location.href = response.data.installUrl;
      }
    } catch (error) {
      console.error('Erro ao iniciar instalacao GitHub App:', error);
      showError('Falha ao iniciar instalacao da GitHub App');
    } finally {
      setLoading(false);
    }
  };

  const connectRepository = async (repoToConnect = null) => {
    const repoId = repoToConnect?.github_repo_id || selectedRepoId;
    if (!selectedInstallationId || !repoId) {
      showError('Selecione uma instalacao e um repositorio');
      return;
    }

    setLoading(true);
    try {
      const repo = repoToConnect || repositories.find((item) => String(item.github_repo_id) === String(repoId));
      const response = await axios.post(getApiUrl(`github-app/projects/${projectId}/link`), {
        installationId: selectedInstallationId,
        githubRepoId: repoId,
        defaultBranch: repo?.default_branch || null
      });

      if (response.data.success) {
        setConnectedRepo(response.data.data);
        setIsConnected(true);
        showSuccess(`Repositorio ${repo?.full_name || ''} vinculado com sucesso`);
        loadRepositoryData();
      }
    } catch (error) {
      console.error('Erro ao vincular repositorio GitHub App:', error);
      showError('Falha ao vincular repositorio');
    } finally {
      setLoading(false);
    }
  };

  const disconnectRepository = async () => {
    setLoading(true);
    try {
      await axios.delete(getApiUrl(`github-app/projects/${projectId}/link`));
      setIsConnected(false);
      setConnectedRepo(null);
      setBranches([]);
      setIssues([]);
      setPullRequests([]);
      setReleases([]);
      showSuccess('Repositorio desvinculado com sucesso');
      loadInstallations();
    } catch (error) {
      console.error('Erro ao desvincular GitHub App:', error);
      showError('Falha ao desvincular repositorio');
    } finally {
      setLoading(false);
    }
  };

  const syncWithGitHub = async () => {
    setSyncStatus('syncing');
    try {
      await loadRepositoryData();
      setSyncStatus('success');
      showSuccess('Dados GitHub atualizados com sucesso');
    } catch (error) {
      setSyncStatus('error');
    }
    setTimeout(() => setSyncStatus('idle'), 3000);
  };

  if (!isConnected) {
    return (
      <div className="github-card">
        <div className="github-card-header">
          <h3 className="github-card-title">Integracao GitHub</h3>
        </div>
        <div className="github-card-content">
          <div className="github-connect-section">
            <div className="github-icon">GitHub</div>
            <h4>Conecte repositorios dos clientes</h4>
            <p>Use a GitHub App para vincular repositorios autorizados ao projeto.</p>
            <div className="github-connect-actions">
              <button
                className="btn-github-connect"
                onClick={connectToGitHubApp}
                disabled={loading || config?.configured === false}
              >
                {loading ? 'Carregando...' : 'Instalar ou atualizar GitHub App'}
              </button>
              <button
                className="btn-connect-repo"
                type="button"
                onClick={loadInstallations}
                disabled={loading}
              >
                Atualizar conexoes GitHub
              </button>
              <button
                className="btn-github-diagnostic"
                type="button"
                onClick={runDiagnostics}
                disabled={loading}
              >
                Diagnosticar conexao
              </button>
            </div>
            {connectionMessage && (
              <p className="repository-description">{connectionMessage}</p>
            )}
            {diagnostics && (
              <div className="repository-description">
                <div>Configurado: {diagnostics.config?.configured ? 'sim' : 'nao'}</div>
                <div>GitHub API: {diagnostics.githubApi?.canListInstallations ? 'ok' : 'falhou'}</div>
                <div>Instalacoes no GitHub: {diagnostics.githubApi?.installationsCount}</div>
                <div>Instalacoes no banco: {diagnostics.database?.localInstallationsCount}</div>
                <div>Repositorios no banco: {diagnostics.database?.localRepositoriesCount}</div>
                {diagnostics.githubApi?.error && (
                  <div>Erro GitHub: {diagnostics.githubApi.error}</div>
                )}
                {diagnostics.database?.error && (
                  <div>Erro banco: {diagnostics.database.error}</div>
                )}
              </div>
            )}
            {config?.configured === false && (
              <p className="repository-description">
                GitHub App ainda nao configurada no ambiente.
              </p>
            )}
          </div>

          {installations.length > 0 && (
            <div className="github-setup-panel">
              <div className="github-step-header">
                <span className="github-step-number">1</span>
                <div>
                  <h4>Conta GitHub instalada</h4>
                  <p>Escolha a conta ou organizacao onde o cliente instalou a GitHub App.</p>
                </div>
              </div>

              <select
                className="github-select"
                value={selectedInstallationId}
                onChange={(event) => {
                  setSelectedInstallationId(event.target.value);
                  setSelectedRepoId('');
                  setRepositories([]);
                }}
              >
                <option value="">Selecione uma conta GitHub</option>
                {installations.map((installation) => (
                  <option key={installation.installation_id} value={installation.installation_id}>
                    {installation.account_login}
                  </option>
                ))}
              </select>

              {selectedInstallation && (
                <div className="github-installation-summary">
                  <div>
                    <strong>{selectedInstallation.account_login}</strong>
                    <span>{selectedInstallation.account_type || 'Conta GitHub'}</span>
                  </div>
                  <span className="github-status-badge">
                    {selectedInstallationNeedsClaim ? 'Pendente' : 'Conectada'}
                  </span>
                </div>
              )}

              {selectedInstallationId && (
                selectedInstallationNeedsClaim ? (
                  <button
                    className="btn-connect-repo"
                    type="button"
                    onClick={() => claimSelectedInstallation(selectedInstallation.installation_id)}
                    disabled={installationClaiming}
                  >
                    {installationClaiming ? 'Confirmando...' : 'Confirmar conexao nesta conta'}
                  </button>
                ) : (
                  <p className="repository-description">
                    Conexao confirmada. Agora selecione o repositorio autorizado para vincular ao projeto.
                  </p>
                )
              )}
            </div>
          )}

          {selectedInstallationId && !selectedInstallationNeedsClaim && (
            <div className="github-setup-panel">
              <div className="github-step-header">
                <span className="github-step-number">2</span>
                <div>
                  <h4>Repositorios autorizados</h4>
                  <p>Estes sao os repositorios liberados pelo cliente na instalacao da GitHub App.</p>
                </div>
              </div>

              {loading && repositories.length === 0 && (
                <p className="repository-description">Carregando repositorios autorizados...</p>
              )}

              {!loading && repositories.length === 0 && (
                <div className="github-empty-repositories">
                  <strong>Nenhum repositorio autorizado encontrado.</strong>
                  <p>
                    Volte ao GitHub e confira se a app foi instalada em pelo menos um repositorio.
                    Depois clique em Atualizar conexoes GitHub.
                  </p>
                </div>
              )}

              {repositories.length > 0 && (
                <div className="github-repository-list">
                  {repositories.map((repo) => (
                    <div key={repo.github_repo_id} className="repository-item">
                      <div className="repository-info">
                        <div>
                          <div className="repository-name">{repo.full_name}</div>
                          <p className="repository-description">
                            Branch principal: {repo.default_branch || 'nao informada'}
                          </p>
                        </div>
                        <button
                          className="btn-connect-repo"
                          onClick={() => {
                            setSelectedRepoId(String(repo.github_repo_id));
                            connectRepository(repo);
                          }}
                          disabled={loading}
                        >
                          Vincular ao projeto
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedInstallationId && repositories.length > 0 && (
            <div className="github-setup-panel">
              <div className="github-step-header">
                <span className="github-step-number">3</span>
                <div>
                  <h4>Resultado esperado</h4>
                  <p>Ao vincular, este projeto passara a mostrar branches, pull requests, issues e releases do repositorio escolhido.</p>
                </div>
              </div>
            </div>
          )}

          {installations.length === 0 && !loading && (
            <div className="github-empty-repositories">
              <strong>Nenhuma conta GitHub conectada.</strong>
              <p>
                Instale a GitHub App em uma conta ou organizacao e depois clique em Atualizar conexoes GitHub.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="github-integration">
      <div className="github-card">
        <div className="github-card-header">
          <div className="github-status-header">
            <h3 className="github-card-title">GitHub App conectado</h3>
            <span className="github-status-badge">Ativo</span>
          </div>
        </div>
        <div className="github-card-content">
          <div className="github-repo-info">
            <div className="github-repo-details">
              <div className="github-repo-icon">Repo</div>
              <div>
                <div className="github-repo-name">{connectedRepo?.full_name}</div>
                <div className="github-repo-description">
                  Branch padrao: {connectedRepo?.default_branch || 'nao informada'}
                </div>
              </div>
            </div>
            <div className="github-actions">
              <button
                className={`btn-sync ${syncStatus === 'syncing' ? 'syncing' : ''}`}
                onClick={syncWithGitHub}
                disabled={syncStatus === 'syncing' || loading}
              >
                {syncStatus === 'syncing' ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              <button className="btn-connect-repo" onClick={disconnectRepository} disabled={loading}>
                Desvincular
              </button>
            </div>
          </div>

          <div className="github-setup-panel">
            <div className="github-step-header">
              <span className="github-step-number">3</span>
              <div>
                <h4>Repositorio vinculado ao projeto</h4>
                <p>O QAdoro agora pode consultar dados deste repositorio para contexto de QA.</p>
              </div>
            </div>
            <div className="github-installation-summary">
              <div>
                <strong>{connectedRepo?.full_name}</strong>
                <span>Branch padrao: {connectedRepo?.default_branch || 'nao informada'}</span>
              </div>
              <span className="github-status-badge">Vinculado</span>
            </div>
          </div>
        </div>
      </div>

      <div className="github-card">
        <div className="github-card-header">
          <h3 className="github-card-title">Branches ({branches.length})</h3>
        </div>
        <div className="github-card-content">
          <div className="github-list">
            {branches.slice(0, 5).map((branch) => (
              <div key={branch.name} className="github-list-item">
                <div className="github-branch-info">
                  <span className="github-branch-name">{branch.name}</span>
                  {(branch.name === 'main' || branch.name === 'master') && (
                    <span className="github-badge">Principal</span>
                  )}
                </div>
              </div>
            ))}
            {branches.length === 0 && <p className="repository-description">Nenhuma branch encontrada.</p>}
          </div>
        </div>
      </div>

      <div className="github-card">
        <div className="github-card-header">
          <h3 className="github-card-title">Pull Requests ({pullRequests.length})</h3>
        </div>
        <div className="github-card-content">
          <div className="github-list">
            {pullRequests.slice(0, 5).map((pull) => (
              <div key={pull.id} className="github-issue-item">
                <div className="github-issue-content">
                  <div className="github-issue-title">
                    <span className="github-issue-number">#{pull.number}</span>
                    <span className="github-issue-text">{pull.title}</span>
                  </div>
                  <div className="github-issue-meta">
                    <span>{pull.head?.ref} para {pull.base?.ref}</span>
                    <span>{new Date(pull.updated_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            ))}
            {pullRequests.length === 0 && <p className="repository-description">Nenhum pull request aberto.</p>}
          </div>
        </div>
      </div>

      <div className="github-card">
        <div className="github-card-header">
          <h3 className="github-card-title">Issues ({issues.length})</h3>
        </div>
        <div className="github-card-content">
          <div className="github-list">
            {issues.slice(0, 5).map((issue) => (
              <div key={issue.id} className="github-issue-item">
                <div className="github-issue-content">
                  <div className="github-issue-title">
                    <span className="github-issue-number">#{issue.number}</span>
                    <span className="github-issue-text">{issue.title}</span>
                  </div>
                  <div className="github-issue-meta">
                    <span>{issue.state}</span>
                    <span>{new Date(issue.updated_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
              </div>
            ))}
            {issues.length === 0 && <p className="repository-description">Nenhuma issue aberta.</p>}
          </div>
        </div>
      </div>

      <div className="github-card">
        <div className="github-card-header">
          <h3 className="github-card-title">Releases ({releases.length})</h3>
        </div>
        <div className="github-card-content">
          <div className="github-list">
            {releases.slice(0, 5).map((release) => (
              <div key={release.id} className="github-list-item">
                <div className="github-branch-info">
                  <span className="github-branch-name">{release.tag_name}</span>
                  {release.name && <span>{release.name}</span>}
                </div>
              </div>
            ))}
            {releases.length === 0 && <p className="repository-description">Nenhuma release encontrada.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GitHubIntegration;
